import React, { useState } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';

// Components
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { Highlights } from './components/Highlights';
import { ProgramPillars } from './components/ProgramPillars';
import { Benefits } from './components/Benefits';
import { Curriculum } from './components/Curriculum';
import { Testimonials } from './components/Testimonials';
import { Pricing } from './components/Pricing';
import { FAQ } from './components/FAQ';
import { CTA } from './components/CTA';
import { Footer } from './components/Footer';
import { ApplicationModal } from './components/ApplicationModal';
import { PaymentModal } from './components/PaymentModal';
import { AdminDashboard } from './components/AdminDashboard';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './components/ui/dialog';
import { Button } from './components/ui/button';

// Mock Data
import { programData } from './data/mock';

// Payment Selection Modal
function PaymentSelectionModal({ isOpen, onClose, onSelectPayment }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Select Payment Option</DialogTitle>
          <DialogDescription>
            Choose how you'd like to pay for the program
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 py-4">
          {programData.investmentOptions.map((option, index) => (
            <div
              key={index}
              className={`border-2 rounded-lg p-4 cursor-pointer hover:border-green-600 transition-colors ${
                option.popular ? 'border-purple-500' : option.isTest ? 'border-blue-500' : 'border-gray-200'
              }`}
              onClick={() => onSelectPayment(option)}
            >
              {option.badge && (
                <span className={`${option.isTest ? 'bg-blue-500' : 'badge-scholarship'} text-xs mb-2 inline-block px-2 py-1 rounded text-white`}>
                  {option.badge}
                </span>
              )}
              <h3 className="font-bold text-lg mb-2">{option.name}</h3>
              <p className="text-2xl font-bold text-green-700 mb-3">{option.price}</p>
              {option.totalPrice && (
                <p className="text-sm text-gray-600">Total: {option.totalPrice}</p>
              )}
              <Button
                className="w-full mt-3 btn-primary-green btn-transition"
                size="sm"
              >
                Select
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Landing Page Component
function LandingPage({ onApplyClick }) {
  return (
    <>
      <Header onApplyClick={onApplyClick} />
      
      <main>
        <Hero data={programData.hero} onApplyClick={onApplyClick} />
        
        <Highlights highlights={programData.highlights} />
        
        <ProgramPillars pillars={programData.programPillars} />
        
        <Benefits benefits={programData.benefits} />
        
        <div id="curriculum">
          <Curriculum curriculum={programData.curriculum} />
        </div>
        
        <div id="testimonials">
          <Testimonials testimonials={programData.testimonials} />
        </div>
        
        <div id="pricing">
          <Pricing 
            options={programData.investmentOptions} 
            onApplyClick={onApplyClick}
          />
        </div>
        
        <div id="faq">
          <FAQ faqs={programData.faqs} />
        </div>
        
        <CTA onApplyClick={onApplyClick} />
      </main>
      
      <Footer />
    </>
  );
}

function App() {
  const [isApplicationModalOpen, setIsApplicationModalOpen] = useState(false);
  const [isPaymentSelectionOpen, setIsPaymentSelectionOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedPaymentOption, setSelectedPaymentOption] = useState(null);
  const [currentApplicationId, setCurrentApplicationId] = useState(null);
  const [currentApplicantData, setCurrentApplicantData] = useState(null);

  const handleApplyClick = () => {
    setIsApplicationModalOpen(true);
  };

  const handlePaymentRequired = (applicationId, applicantData) => {
    setCurrentApplicationId(applicationId);
    setCurrentApplicantData(applicantData);
    setIsPaymentSelectionOpen(true);
  };

  const handleSelectPayment = (paymentOption) => {
    setSelectedPaymentOption(paymentOption);
    setIsPaymentSelectionOpen(false);
    setIsPaymentModalOpen(true);
  };

  const handlePaymentClose = (success) => {
    setIsPaymentModalOpen(false);
    setSelectedPaymentOption(null);
    setCurrentApplicationId(null);
    setCurrentApplicantData(null);
  };

  return (
    <BrowserRouter>
      <div className="App">
        <Toaster position="top-right" richColors />
        
        <Routes>
          <Route path="/" element={<LandingPage onApplyClick={handleApplyClick} />} />
          <Route path="/admin" element={<AdminDashboard />} />
        </Routes>
        
        <ApplicationModal 
          isOpen={isApplicationModalOpen}
          onClose={() => setIsApplicationModalOpen(false)}
          onPaymentRequired={handlePaymentRequired}
        />

        <PaymentSelectionModal
          isOpen={isPaymentSelectionOpen}
          onClose={() => setIsPaymentSelectionOpen(false)}
          onSelectPayment={handleSelectPayment}
        />

        <PaymentModal
          isOpen={isPaymentModalOpen}
          onClose={handlePaymentClose}
          applicationId={currentApplicationId}
          paymentOption={selectedPaymentOption}
          applicantData={currentApplicantData}
        />
      </div>
    </BrowserRouter>
  );
}

export default App;
